import { useCallback, useMemo, useState } from 'react';

import dlv from 'dlv';

import useDebouncedEffectOnUpdates from 'hooks/use-debounce-effect-on-updates';
import useEffectOnUpdates from 'hooks/use-effect-on-updates';
import { useAuthContext } from 'state/auth';
import { UserDetails } from 'state/auth/hooks/use-current-user';
import { ILoyaltyCategory, ILoyaltyTier } from 'state/loyalty/types';

export type SelectedRewardsTierOptions = Partial<{
  onSetRewardsTierError: () => void;
  loyaltyTiers: ILoyaltyTier[];
}>;

export const useSelectedRewardTier = (
  loyaltyCategories: ILoyaltyCategory[] | null = [],
  setSelectedRewardTier: (id: string) => void,
  { loyaltyTiers }: SelectedRewardsTierOptions = {}
) => {
  const { user } = useAuthContext();

  const loyaltyTier = dlv(user || {}, 'details.loyaltyTier');

  const [selectedRewardTierId, setSelectedRewardTierIdOnChange] = useState<
    UserDetails['details']['loyaltyTier']
  >(loyaltyTier);

  useEffectOnUpdates(() => {
    if (selectedRewardTierId !== loyaltyTier) {
      setSelectedRewardTierIdOnChange(loyaltyTier);
    }
  }, [loyaltyTier]);

  /**
   * This effect controls calling the `setSelectedRewardTier` method with the user's
   * updated reward tier.
   */
  useDebouncedEffectOnUpdates(
    () => {
      if (!selectedRewardTierId || selectedRewardTierId === loyaltyTier) {
        return;
      }

      setSelectedRewardTier(selectedRewardTierId);
    },
    1000,
    [selectedRewardTierId]
  );

  const onChangeRewardTier = useCallback(
    (loyaltyTierId: string | null) => {
      if (!loyaltyTierId) {
        return;
      }

      setSelectedRewardTierIdOnChange(loyaltyTierId);
    },
    [setSelectedRewardTierIdOnChange]
  );

  const selectedRewardCategoryId = useMemo(() => {
    // if we have loyaltyTiers this is an onboarding update.
    if (loyaltyCategories === null && loyaltyTiers && loyaltyTiers.length) {
      const selectedTier = loyaltyTiers.find(({ _id }) => _id === selectedRewardTierId);
      return selectedTier ? selectedTier._id : selectedTier;
    }
    if (loyaltyCategories && loyaltyCategories.length) {
      const selected = loyaltyCategories.find(({ options }) =>
        options.find(({ _id }) => _id === selectedRewardTierId)
      );
      return selected ? selected._id : selected;
    }
    return undefined;
  }, [loyaltyTiers, loyaltyCategories, selectedRewardTierId]);

  return {
    selectedRewardCategoryId,
    selectedRewardTierId,
    onChangeRewardTier,
  };
};
